import "./Yearly.css"
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import APIURL from "../../common/Config";
import Header from "../header/header";
import Footer from "../footer/footer";

const SagittariusCard = () => {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inMemoryCacheRef = useRef(new Map());
  const [activeIndex, setActiveIndex] = useState(null);

  // Static metadata for SAGITTARIUS
  const staticMeta = {
    title: "AstroBhagya – Free Sagittarius Yearly Horoscope by Best Astrologer",
    description: "Looking for accurate Sagittarius yearly horoscope predictions 2026? Discover free Sagittarius astrology predictions and annual insights by zodiac sign for a better year ahead.",
    keywords: "Free Sagittarius yearly horoscope predictions 2026, Sagittarius yearly Astrology predictions, Sagittarius annual predictions, Yearly horoscope by Sagittarius zodiac sign",
    author: "Heavenly Expectation Private Limited",
  };

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const fetchSagittariusYearly = useCallback(async (retries = 3) => {
    const dateStr = "20260101"; // 2026 data
    const cacheKey = `sagittarius_yearly_${dateStr}`;
    setError(null);
    setLoading(true);

    // 1. CHECK LOCALSTORAGE
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setHoroscopeData(parsed);
        setLoading(false);
        return;
      } catch (e) {
        console.error("Failed to parse cached data:", e);
      }
    }

    // 2. CHECK IN-MEMORY CACHE
    if (inMemoryCacheRef.current.has(cacheKey)) {
      setHoroscopeData(inMemoryCacheRef.current.get(cacheKey));
      setLoading(false);
      return;
    }

    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn('Fetch request timed out after 15 seconds.');
      }, 15000);

      const response = await fetch(
        `${APIURL}/horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // Find SAGITTARIUS data (id: 38 or sunShine contains "SAGITTARIUS")
        const sagittariusData = data.find(
          item => item.id === 38 || item.sunShine?.toUpperCase().includes("SAGITTARIUS")
        );

        if (sagittariusData) {
          // Cache the data
          inMemoryCacheRef.current.set(cacheKey, sagittariusData);
          localStorage.setItem(cacheKey, JSON.stringify(sagittariusData));
          setHoroscopeData(sagittariusData);
        } else {
          throw new Error("Sagittarius data not found in API response");
        }
      } else {
        throw new Error("Unexpected response format from API");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The network request timed out. Please check your internet connection and try again.');
        console.error("Network timeout error:", error);
      } else {
        setError(`Failed to load yearly horoscope: ${error.message}. Please try again.`);
        console.error("Error fetching Sagittarius yearly data:", error);
      }

      if (retries > 0) {
        console.log(`Retrying fetch for Sagittarius Yearly. Retries left: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchSagittariusYearly(retries - 1);
      } else {
        // FALLBACK DATA - SAGITTARIUS 2026 
        console.log("All retries exhausted. Using fallback data.");
        const fallbackData = {
          sunShine: "Sagittarius 2026: Your year, as the stars align",
          prediction: `<div class="horoscope">
            <p>2026 brings Sagittarius exploration and hidden talent discovery. Balance curiosity with practical outcomes for growth.</p>
            <h2>Love and Relationships</h2>
            <p>Deepen emotional intimacy through shared goals. Meaningful connections built on trust and patience.</p>
            <h2>Career and Finances</h2>
            <p>Innovation drives professional growth. Smart investments ensure financial stability.</p>
            <h2>Health & Wellness</h2>
            <p>Balanced energy prevents fatigue. Yoga and mindfulness maintain focus.</p>
            <h2>Astrological Remedies</h2>
            <ul>
              <li><strong>Turquoise/Aquamarine:</strong> Enhances clarity and calmness.</li>
              <li><strong>Reflection Practice:</strong> Journaling improves insight.</li>
              <li><strong>Thursday Jupiter:</strong> Yellow donations bring fortune.</li>
            </ul>
          </div>`
        };
        setHoroscopeData(fallbackData);
      }
    } finally {
      await minLoadingTime;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSagittariusYearly();
  }, [fetchSagittariusYearly]);

  // Set meta tags
  useEffect(() => {
    document.title = staticMeta.title;

    const setOrCreateMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute("content", content);
      } else {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        metaTag.setAttribute("content", content);
        document.head.appendChild(metaTag);
      }
    };

    setOrCreateMetaTag("description", staticMeta.description);
    setOrCreateMetaTag("keywords", staticMeta.keywords);
    setOrCreateMetaTag("author", staticMeta.author);
    setOrCreateMetaTag("revisit-after", "7 days");
    setOrCreateMetaTag("distribution", "global");
    setOrCreateMetaTag("rating", "general");

    let charsetMeta = document.querySelector("meta[charset]");
    if (!charsetMeta) {
      charsetMeta = document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      document.head.prepend(charsetMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading Sagittarius 2026 Horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => fetchSagittariusYearly()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div>
        <section id="Make">
          <div className="year-container">
            <h4 className="mt-3 mb-3 text-center font-weight-bold">
              {horoscopeData?.sunShine || "Sagittarius 2026: Your year, as the stars align"}
            </h4>
            <div className="icons-wrapper text-center mb-4">
              <img
                src={require("../../assets/Sagittariusss.webp")}
                alt="Sagittarius"
                className="horoscope-icon mx-2"
              />
            </div>

            {horoscopeData?.prediction && (
              <main>
                <div
                  dangerouslySetInnerHTML={{
                    __html: horoscopeData.prediction
                  }}
                />
              </main>
            )}

            <div className="btn-wrapper">
              <Link to="/yearly-horoscope/scorpio" className="btn btn-customs">
                <i className="bi bi-arrow-left"></i> Scorpio Horoscope 2026
              </Link>
              <Link to="/yearly-horoscope/capricorn" className="btn btn-customs">
                <i className="bi bi-arrow-right"></i> Capricorn Horoscope 2026
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ SECTION - SAME AS PREVIOUS */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Sagittarius Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What are the core personality traits of Sagittarius?",
                  answer: "Sagittarius individuals are known for their optimism, adventurous spirit, love of freedom, and philosophical nature. Ruled by Jupiter, they seek growth and exploration.",
                },
                {
                  question: "2. What can Sagittarius expect in love and relationships in 2026?",
                  answer: "2026 deepens emotional connections through shared goals. Trust and patience build meaningful, lasting partnerships.",
                },
                {
                  question: "3. What does 2026 look like for Sagittarius career growth?",
                  answer: "Innovation and initiative drive professional success. Networking and strategic planning bring recognition.",
                },
                {
                  question: "4. What health advice for Sagittarius in 2026?",
                  answer: "Balanced energy prevents fatigue. Outdoor activities, yoga, and mindfulness maintain vitality.",
                },
                {
                  question: "5. Key astrological remedies for Sagittarius?",
                  answer: "Turquoise/Aquamarine, reflection practice, and Thursday Jupiter remedies enhance clarity and fortune.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SagittariusCard; 
